import { NavLink } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import type { SvgIconComponent } from "@mui/icons-material";
import { Tooltip } from "@/components/ui/Tooltip";
import { cn } from "@/utils/cn";
import type { NavItem } from "@/components/layout/Sidebar";

const ACCENT_ACTIVE: Record<"violet" | "slate", string> = {
  violet:
    "bg-primary-50 text-primary-700 border-primary-500 dark:bg-primary-950/80 dark:text-primary-200 dark:border-primary-400",
  slate:
    "bg-slate-100 text-slate-900 border-slate-400 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-500",
};

export function NavLinkRow({
  item,
  collapsed,
  locked,
  accent,
  inFlyout = false,
  iconOverride,
}: {
  item: NavItem;
  collapsed: boolean;
  locked?: boolean;
  accent: "violet" | "slate";
  inFlyout?: boolean;
  iconOverride?: SvgIconComponent;
}) {
  const Icon = iconOverride ?? item.icon;
  const content = (
    <>
      <Icon className="shrink-0 opacity-90" fontSize="small" />
      {(!collapsed || inFlyout) && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {locked && (
            <LockOutlinedIcon
              className="shrink-0 opacity-50"
              sx={{ fontSize: 14 }}
            />
          )}
        </>
      )}
    </>
  );

  const collapsedIconOnly = collapsed && !inFlyout;
  const collapsedItemClass = collapsedIconOnly
    ? "w-full justify-center !border-l-0 !px-2"
    : "";

  if (locked) {
    const lockedNode = (
      <span
        className={cn(
          "workspace-nav-item workspace-nav-item--locked",
          collapsedItemClass,
          inFlyout && "workspace-nav-item--flyout",
        )}
      >
        {content}
      </span>
    );

    return (
      <Tooltip
        content={
          collapsedIconOnly
            ? `${item.label} (upgrade required)`
            : "Upgrade your plan to unlock"
        }
      >
        {collapsedIconOnly ? (
          <span className="block w-full">{lockedNode}</span>
        ) : (
          lockedNode
        )}
      </Tooltip>
    );
  }

  const activeClass = ACCENT_ACTIVE[accent];

  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          "workspace-nav-item",
          inFlyout && "workspace-nav-item--flyout",
          collapsedItemClass,
          isActive &&
            cn(
              "workspace-nav-item--active",
              collapsedIconOnly ? activeClass : cn("border-l-2", activeClass),
            ),
        )
      }
    >
      {content}
    </NavLink>
  );

  return (
    <Tooltip content={collapsedIconOnly ? item.label : undefined}>
      {collapsedIconOnly ? <span className="block w-full">{link}</span> : link}
    </Tooltip>
  );
}
